/**
 * Dropdown menu: Radix primitives (keyboard, focus and ARIA handled), styled to
 * the section 11.0 tokens, the same way shadcn/ui wraps them.
 */
import * as Menu from "@radix-ui/react-dropdown-menu";
import type { ComponentPropsWithoutRef } from "react";

export const DropdownMenu = Menu.Root;
export const DropdownMenuTrigger = Menu.Trigger;
export const DropdownMenuRadioGroup = Menu.RadioGroup;

export function DropdownMenuContent({
  className = "",
  sideOffset = 8,
  align = "end",
  ...props
}: ComponentPropsWithoutRef<typeof Menu.Content>) {
  return (
    <Menu.Portal>
      <Menu.Content
        sideOffset={sideOffset}
        align={align}
        className={`z-50 min-w-60 rounded-card border border-line bg-panel p-2 text-text ${className}`.trim()}
        {...props}
      />
    </Menu.Portal>
  );
}

export function DropdownMenuLabel({
  className = "",
  ...props
}: ComponentPropsWithoutRef<typeof Menu.Label>) {
  return (
    <Menu.Label
      className={`px-3 pt-2 pb-1 text-[12.5px] font-medium uppercase tracking-[0.08em] text-muted ${className}`.trim()}
      {...props}
    />
  );
}

export function DropdownMenuSeparator() {
  return <Menu.Separator className="my-2 h-px bg-line" />;
}

const itemClass =
  "flex cursor-pointer select-none items-center gap-2 rounded-row px-3 py-2 text-sm outline-none data-[highlighted]:bg-brand-tint data-[highlighted]:text-brand-ink-on-tint";

export function DropdownMenuItem({
  className = "",
  ...props
}: ComponentPropsWithoutRef<typeof Menu.Item>) {
  return <Menu.Item className={`${itemClass} ${className}`.trim()} {...props} />;
}

export function DropdownMenuRadioItem({
  className = "",
  children,
  ...props
}: ComponentPropsWithoutRef<typeof Menu.RadioItem>) {
  return (
    <Menu.RadioItem
      className={`${itemClass} data-[state=checked]:font-semibold data-[state=checked]:text-brand-ink ${className}`.trim()}
      {...props}
    >
      <span
        aria-hidden="true"
        className="flex size-3 items-center justify-center rounded-pill border border-line p-0.5"
      >
        <Menu.ItemIndicator className="block size-full rounded-pill bg-brand" />
      </span>
      {children}
    </Menu.RadioItem>
  );
}
